import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Plus, Minus, Trash2, ShoppingBag, MapPin, Home as HomeIcon, Briefcase, Store, Package } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import ProductImage from "../components/ProductImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cart = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, updateQuantity, removeItem, totalPrice, clearCart, hydrateImages } = useCart();
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [stockMap, setStockMap] = useState({});
  const [savedLocations, setSavedLocations] = useState([]);
  const [selectedLocId, setSelectedLocId] = useState(null);
  // Merge-or-new choice modal (shown when the customer already has a
  // pending, unconfirmed invoice). Populated by the pending-check.
  const [mergeChoice, setMergeChoice] = useState(null); // { invoice_number, total, total_quantity } | null

  // Load the customer's saved delivery addresses so they can pick which
  // one this order should be delivered to. Default to whichever is marked
  // is_default; if none, the first entry.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/saved-locations`);
        const locs = data.locations || [];
        setSavedLocations(locs);
        const def = locs.find((l) => l.is_default) || locs[0];
        if (def) setSelectedLocId(def.id);
      } catch { /* silent — feature is optional */ }
    })();
  }, [user]);

  // Fetch live stock for every item in the cart, and backfill missing
  // image fields for items that were added before image_path was stored.
  useEffect(() => {
    if (items.length === 0) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products`);
        const map = {};
        const imgMap = {};
        (data || []).forEach((p) => {
          map[p.id] = Math.max(0, Number(p.stock || 0));
          imgMap[p.id] = { image_path: p.image_path, image_path2: p.image_path2, color: p.color };
        });
        setStockMap(map);
        // Patch any cart item missing image_path so the picture appears.
        const needsPatch = items.some((it) => !it.image_path && imgMap[it.product_id]?.image_path);
        if (needsPatch) hydrateImages(imgMap);
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const getStock = (pid) => (stockMap[pid] ?? Infinity);

  const handleCheckout = async () => {
    if (!user) {
      toast.error("الرجاء تسجيل الدخول أولاً");
      navigate("/login");
      return;
    }

    if (items.length === 0) {
      toast.error("السلة فارغة");
      return;
    }

    // Check for an existing pending (unconfirmed & unprinted) invoice —
    // if found, ask the customer whether to merge or start a fresh one.
    try {
      const { data } = await axios.get(`${API}/orders/pending-mergeable`);
      if (data.has_pending && data.order) {
        setMergeChoice(data.order);
        return; // Wait for customer's decision
      }
    } catch { /* if the check fails just proceed with default merge behavior */ }

    await submitOrder(false);
  };

  const submitOrder = async (forceNew) => {
    setSubmitting(true);
    try {
      const selectedLoc = savedLocations.find((l) => l.id === selectedLocId) || null;
      const payload = {
        items: items.map((it) => ({
          product_id: it.product_id,
          name: it.name,
          name_ar: it.name_ar,
          price: it.price,
          quantity: it.quantity,
        })),
        notes: notes.trim(),
        force_new_invoice: !!forceNew,
        delivery_location_id: selectedLoc?.id,
        delivery_label: selectedLoc?.name,
        delivery_lat: selectedLoc?.lat,
        delivery_lng: selectedLoc?.lng,
        delivery_area_note: selectedLoc?.area_note,
        delivery_alt_phone: selectedLoc?.alt_phone,
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      toast.success(
        data.merged
          ? "تم إضافة المنتجات لفاتورتك الحالية"
          : "تم استلام طلبك بنجاح! الدفع عند الاستلام"
      );
      clearCart();
      setMergeChoice(null);
      navigate(`/order-success/${data.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "حدث خطأ في إنشاء الطلب";
      toast.error(typeof msg === "string" ? msg : "حدث خطأ");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E4EBE6]">
        <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-3xl px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            data-testid="cart-back"
            className="p-2 hover:bg-[#F3F6F4] rounded-full transition-colors"
          >
            <ArrowRight className="w-5 h-5 text-[#2D4A3E]" />
          </button>
          <h1 className="font-heading font-bold text-2xl text-[#2D4A3E]">سلة التسوق</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto md:max-w-2xl lg:max-w-3xl px-4 py-6 space-y-6">
        {items.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className="w-20 h-20 text-[#E4EBE6] mx-auto mb-4" />
            <p className="text-lg text-[#5C6E64] mb-6">السلة فارغة</p>
            <Link
              to="/"
              data-testid="continue-shopping"
              className="inline-block bg-[#2D4A3E] hover:bg-[#233A30] text-white font-medium px-8 py-3 rounded-full transition-colors"
            >
              تصفح المنتجات
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-3" data-testid="cart-items">
              {items.map((item) => (
                <motion.div
                  key={item.product_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={`cart-item-${item.product_id}`}
                  className="bg-[#FAFCFA] border border-[#E4EBE6] rounded-2xl p-4 flex items-center gap-4"
                >
                  <div
                    className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{ backgroundColor: item.color }}
                  >
                    {(item.image_path || item.image_path2) ? (
                      <ProductImage
                        image={item.image_path}
                        image2={item.image_path2}
                        alt={item.name_ar}
                        className="w-full h-full"
                      />
                    ) : (
                      <span className="font-bold text-sm text-center px-1">
                        {item.name_ar.slice(0, 10)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#111814]">{item.name_ar}</h3>
                    <p className="text-sm text-[#5C6E64]">{Number(item.price).toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        data-testid={`cart-decrease-${item.product_id}`}
                        className="w-8 h-8 rounded-full bg-white border border-[#E4EBE6] flex items-center justify-center hover:bg-[#E4EBE6]"
                      >
                        <Minus className="w-3 h-3 text-[#2D4A3E]" />
                      </button>
                      <span data-testid={`cart-qty-${item.product_id}`} className="font-bold w-8 text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => {
                          const stock = getStock(item.product_id);
                          if (item.quantity >= stock) {
                            toast.error(`هذا المنتج غير متوفّر حاليّاً بكمية أكثر من ${stock}`);
                            return;
                          }
                          updateQuantity(item.product_id, item.quantity + 1);
                        }}
                        disabled={item.quantity >= getStock(item.product_id)}
                        data-testid={`cart-increase-${item.product_id}`}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                          item.quantity >= getStock(item.product_id)
                            ? "bg-[#BAE6FD] cursor-not-allowed"
                            : "bg-[#2D4A3E] hover:bg-[#233A30]"
                        }`}
                      >
                        <Plus className="w-3 h-3 text-white" />
                      </button>
                    </div>
                    {item.quantity >= getStock(item.product_id) && getStock(item.product_id) !== Infinity && (
                      <p data-testid={`cart-stock-warning-${item.product_id}`} className="text-[10px] text-[#a06e10] bg-[#FFF8EC] border border-[#F5A623] rounded-lg px-2 py-1 mt-1 inline-block">
                        غير متوفّر حاليّاً بكمية أكثر — هذا الحدّ الأقصى في المخزن
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="font-bold text-[#E85D45]">
                      {(item.price * item.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع
                    </span>
                    <button
                      onClick={() => removeItem(item.product_id)}
                      data-testid={`cart-remove-${item.product_id}`}
                      className="text-[#5C6E64] hover:text-[#E85D45] transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="bg-[#FAFCFA] border border-[#E4EBE6] rounded-2xl p-4 space-y-4">
              {/* Saved-locations picker — let the customer point this order
                  at one of their addresses (home/work/market) so the driver
                  knows where to go. Hidden if none saved yet. */}
              {savedLocations.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold text-[#111814] flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-[#E85D45]" />
                      موقع التوصيل
                    </label>
                    <Link
                      to="/my-locations"
                      data-testid="manage-locations-link"
                      className="text-xs font-bold text-[#2563EB] hover:underline"
                    >
                      إدارة المواقع
                    </Link>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {savedLocations.map((loc) => {
                      const Icon = { home: HomeIcon, work: Briefcase, market: Store, warehouse: Package }[loc.label_type] || MapPin;
                      const active = selectedLocId === loc.id;
                      return (
                        <button
                          key={loc.id}
                          onClick={() => setSelectedLocId(loc.id)}
                          data-testid={`pick-loc-${loc.id}`}
                          className={`flex items-center gap-2 p-3 rounded-xl border-2 text-right transition-all ${active ? "border-[#15803D] bg-[#F0FDF4]" : "border-[#E4EBE6] bg-white hover:border-[#BAE6FD]"}`}
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-[#15803D] text-white" : "bg-[#F0F9FF] text-[#2D4A3E]"}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-[#111814] truncate flex items-center gap-1">
                              {loc.name}
                              {loc.is_default && <span className="text-[10px] text-[#92400E]">⭐</span>}
                            </p>
                            {loc.area_note && (
                              <p className="text-[11px] text-[#5B6B62] truncate">{loc.area_note}</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {savedLocations.length === 0 && (
                <Link
                  to="/my-locations"
                  data-testid="add-first-loc-link"
                  className="flex items-center justify-between bg-[#FFF7ED] border border-[#FED7AA] rounded-2xl p-3 hover:bg-[#FEF3C7] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#E85D45]" />
                    <span className="text-sm font-bold text-[#92400E]">احفظ موقع التوصيل (اختياري)</span>
                  </div>
                  <span className="text-xs text-[#92400E] underline">إضافة</span>
                </Link>
              )}
              <div>
                <label className="block text-sm font-medium text-[#111814] mb-2">
                  ملاحظة
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="checkout-notes"
                  className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
                  rows="3"
                  placeholder="اكتب ملاحظتك هنا..."
                />
              </div>
            </div>

            <div className="bg-[#2D4A3E] text-white rounded-2xl p-6 space-y-4">
              <div className="flex justify-between text-lg">
                <span>المجموع</span>
                <span data-testid="cart-total" className="font-bold text-2xl">
                  {totalPrice.toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع
                </span>
              </div>
              <p className="text-sm opacity-80 text-center">
                💵 الدفع عند الاستلام
              </p>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleCheckout}
                disabled={submitting}
                data-testid="checkout-submit"
                className="w-full bg-[#E85D45] hover:bg-[#d54d35] font-bold py-4 rounded-full transition-colors disabled:opacity-50"
              >
                {submitting ? "جاري إرسال الطلب..." : "تأكيد الطلب"}
              </motion.button>
            </div>
          </>
        )}
      </main>

      {/* Merge-vs-New choice modal — shown when a pending invoice already exists */}
      {mergeChoice && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="merge-choice-overlay"
          onClick={() => !submitting && setMergeChoice(null)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="merge-choice-modal"
            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
            dir="rtl"
          >
            <div className="bg-gradient-to-l from-[#F5A623] to-[#E85D45] text-white p-4 text-center">
              <div className="text-4xl mb-1">🧾</div>
              <h3 className="font-black text-lg">عندك فاتورة قيد المعالجة</h3>
              <p className="text-xs opacity-95 mt-1">
                فاتورة رقم <span dir="ltr" className="font-bold">#{mergeChoice.invoice_number}</span>
                {" — "}
                <span dir="ltr" className="font-bold">{Number(mergeChoice.total || 0).toLocaleString("en-US")}</span> د.ع
              </p>
            </div>
            <div className="p-4 space-y-2.5">
              <p className="text-sm text-center text-[#5C6E64]">
                الطلبات الجديدة راح تنضاف على نفس فاتورتك
              </p>
              <button
                onClick={() => submitOrder(false)}
                disabled={submitting}
                data-testid="merge-choice-merge"
                className="w-full bg-[#15803D] hover:bg-[#126B32] disabled:opacity-60 text-white font-black py-3 rounded-2xl flex items-center justify-center gap-2 transition-colors"
              >
                ➕ أضف على فاتورتي
              </button>
              <button
                onClick={() => setMergeChoice(null)}
                disabled={submitting}
                data-testid="merge-choice-cancel"
                className="w-full text-sm text-[#5C6E64] hover:text-[#111814] py-2 transition-colors"
              >
                إلغاء
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Cart;
